import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { CONTRACT_VERSION } from '@gadenin/contract';
import { createBeaconAnalytics } from '../infrastructure/analytics/BeaconAnalytics';
import { createBlogParticipationClient } from '../infrastructure/blog/BlogParticipationClient';
import { BlogParticipation } from '../presentation/blog/BlogParticipation';
import { BlogShare } from '../presentation/blog/BlogShare';
import { activateYouTubeEmbeds } from '../presentation/blog/youtubeEmbed';

/**
 * ブログページの progressive enhancement エントリ。
 *
 * 本文・一覧・検索・絞り込みは **Worker が組んだ HTML** で完結しており、
 * JS 無しで読めるし絞り込みもできる (素の form とリンク)。
 * このスクリプトが足すのは、素の HTML では表せないものだけ:
 * コメント・helpful・通報 (詳細)、Web Share / コピー、計測。
 * mount 先が無ければ黙って何もしない。
 */
function viewportKind(): 'sp' | 'tablet' | 'pc' {
  if (typeof window === 'undefined') return 'pc';
  if (window.innerWidth < 640) return 'sp';
  return window.innerWidth < 1024 ? 'tablet' : 'pc';
}

const analytics = createBeaconAnalytics();
analytics.setContext({ platform: 'web', viewport: viewportKind(), isPwa: false, appVersion: CONTRACT_VERSION });

// --- /blog 一覧: 表示と絞り込みの計測だけ (描画はサーバが済ませている) ---
const listRoot = document.getElementById('blog-list-root');
if (listRoot) {
  analytics.track({
    name: 'web_blog_list_view',
    articleCount: Number(listRoot.dataset['total'] ?? 0),
    filter: listRoot.dataset['filter'] ?? 'none',
  });

  // 絞り込みは素のリンク。遷移前に beacon で送る
  document.querySelectorAll<HTMLAnchorElement>('a[data-blog-filter]').forEach((anchor) => {
    anchor.addEventListener('click', () => {
      analytics.track({
        name: 'web_blog_tag_filter',
        kind: anchor.dataset['blogFilter'] ?? 'tag',
        resultCount: Number(listRoot.dataset['total'] ?? 0),
      });
    });
  });

  // 検索は送信時に文字数だけ送る (生文字列は送らない)
  const form = document.querySelector<HTMLFormElement>('.blog-filters__form');
  form?.addEventListener('submit', () => {
    const input = form.querySelector<HTMLInputElement>('input[name="q"]');
    const length = input?.value.length ?? 0;
    if (length > 0) {
      analytics.track({ name: 'web_blog_search_query', queryLength: length, resultCount: -1 });
    }
  });
}

// --- /blog/:slug 詳細: 参加 UI と共有 ---
const article = document.querySelector<HTMLElement>('article[data-article-id]');
if (article) {
  const articleId = article.dataset['articleId']!;
  const slug = article.dataset['articleSlug'] ?? '';
  analytics.track({ name: 'web_blog_article_view', articleId, slug });

  // YouTube のその場再生 (押されるまで Google へ通信しない)。
  // 再生数の計測はイベントを新設してから足す (共有イベントの流用は誤った数字になる)
  activateYouTubeEmbeds(document);

  // 目次クリックの計測 (サーバが描いた目次リンクへ後付け)
  document.querySelectorAll<HTMLAnchorElement>('.blog-toc a').forEach((anchor, index) => {
    anchor.addEventListener('click', () => {
      analytics.track({ name: 'web_blog_toc_click', articleId, position: index + 1 });
    });
  });

  // 関連記事クリックの計測
  document.querySelectorAll<HTMLAnchorElement>('section[aria-label="関連記事"] a.blog-card').forEach((anchor, index) => {
    anchor.addEventListener('click', () => {
      const to = new URL(anchor.href).pathname.replace('/blog/', '');
      analytics.track({ name: 'web_blog_related_click', fromArticleId: articleId, toArticleId: to, position: index + 1 });
    });
  });

  // X / LINE (静的リンク) の計測
  document.querySelectorAll<HTMLAnchorElement>('.blog-share a[href^="https://twitter.com"]').forEach((anchor) => {
    anchor.addEventListener('click', () => analytics.track({ name: 'web_blog_share', articleId, method: 'x' }));
  });
  document.querySelectorAll<HTMLAnchorElement>('.blog-share a[href^="https://social-plugins.line.me"]').forEach((anchor) => {
    anchor.addEventListener('click', () => analytics.track({ name: 'web_blog_share', articleId, method: 'line' }));
  });

  const shareRoot = document.getElementById('blog-share-root');
  if (shareRoot) {
    createRoot(shareRoot).render(
      <StrictMode>
        <BlogShare
          articleId={articleId}
          canonical={shareRoot.dataset['canonical'] ?? window.location.href}
          title={shareRoot.dataset['shareTitle'] ?? document.title}
          analytics={analytics}
        />
      </StrictMode>,
    );
  }

  const participationRoot = document.getElementById('blog-participation-root');
  if (participationRoot) {
    const client = createBlogParticipationClient();
    const signInHref = `/signin?next=${encodeURIComponent(window.location.pathname)}`;
    createRoot(participationRoot).render(
      <StrictMode>
        <BlogParticipation articleId={articleId} client={client} analytics={analytics} signInHref={signInHref} />
      </StrictMode>,
    );
  }
}
